import { useEffect, useMemo, useState } from 'react'
import FuelReportTable from '../components/FuelTables/FuelReportTable'
import { useQuery } from 'react-query'
import { fetchFuelReport, fetchReportTermLimits } from '../../../../api/newApi'
import { useChosenCompany, useUser, useUserContext } from '../../../../context/UserProvider'
import { ConsolidateInvoiceSelect } from '../../../cars/components/V3/_shared/ConsolidateInvoiceSelect'
import { ExcelExportButton } from '../components/ExcelExporter'
import { excelFuelReportColumns } from '../components/FuelTables/excelUtils'
import type { Fuel } from '../../../../types'
import { monthInputValue, subOneYearMonth, todayMonth, valueToDate } from '../../../../utils/helpers/helpers.reports'
import { DatePicker } from '../../../../components/DatePicker'

const clampMonth = (value: string, min: string, max: string) => {
  if (value < min) return min
  if (value > max) return max
  return value
}

const monthToDateTime = (month: string) => new Date(`${month}-01T00:00:00.000Z`).toISOString()

const FuelReport = () => {
  const contactId = useUser()?.user?.id
  const companyId = useChosenCompany()?.id
  const consolidatedAgreementID = (useUserContext() as any)?.consolidateInvoice?.ConsolidateCode as any

  const defaultToMonth = useMemo(() => todayMonth(), [])
  const defaultFromMonth = useMemo(() => subOneYearMonth(defaultToMonth, 'month'), [defaultToMonth])
  const [toMonth, setToMonth] = useState(defaultToMonth)
  const [fromMonth, setFromMonth] = useState(defaultFromMonth)

  const { data: termLimits, status: termLimitsStatus } = useQuery(
    ['fuelReportTermLimits', companyId, contactId],
    () => fetchReportTermLimits({ companyId, contactId, type: 'fuel' }),
    {
      enabled: !!companyId && !!contactId,
    },
  ) as { data?: { oldest: string; newest: string } }

  const oldestMonth = termLimits?.oldest ? monthInputValue(termLimits.oldest) : undefined
  const newestMonth = termLimits?.newest ? monthInputValue(termLimits.newest) : undefined
  const hasResolvedTermLimits = termLimitsStatus === 'success' || termLimitsStatus === 'error'
  const effectiveMonths = useMemo(() => {
    if (!oldestMonth || !newestMonth) {
      return {
        fromMonth,
        toMonth,
      }
    }

    const nextToMonth = clampMonth(toMonth, oldestMonth, newestMonth)
    const nextDefaultFromMonth = subOneYearMonth(nextToMonth)

    return {
      fromMonth: clampMonth(
        fromMonth === defaultFromMonth || fromMonth > nextToMonth ? nextDefaultFromMonth : fromMonth,
        oldestMonth,
        nextToMonth,
      ),
      toMonth: nextToMonth,
    }
  }, [defaultFromMonth, fromMonth, newestMonth, oldestMonth, toMonth])

  const { data: response, status } = useQuery(
    ['fuelReport', companyId, contactId, consolidatedAgreementID, effectiveMonths.fromMonth, effectiveMonths.toMonth],
    () =>
      fetchFuelReport({
        companyId: companyId || '',
        contactId: contactId || '',
        consolidatedAgreementID,
        fromDate: monthToDateTime(effectiveMonths.fromMonth),
        toDate: monthToDateTime(effectiveMonths.toMonth),
      }),
    {
      enabled: !!companyId && !!contactId && !!consolidatedAgreementID && hasResolvedTermLimits,
    },
  )

  useEffect(() => {
    if (!oldestMonth || !newestMonth) return

    const nextToMonth = effectiveMonths.toMonth
    const nextFromMonth = effectiveMonths.fromMonth

    if (nextToMonth !== toMonth) setToMonth(nextToMonth)
    if (nextFromMonth !== fromMonth) setFromMonth(nextFromMonth)
  }, [effectiveMonths.fromMonth, effectiveMonths.toMonth, fromMonth, newestMonth, oldestMonth, toMonth])

  const responseItems = (response as any)?.items
  const data = (Array.isArray(responseItems) ? responseItems : []) as Fuel[]

  const columns = excelFuelReportColumns()
  const config = {
    columns,
    filename: `Drivstoffrapport_${fromMonth}_${toMonth}.xlsx`,
    sheetName: `Drivstoffrapport_${fromMonth}_${toMonth}`,
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:justify-between">
        <div className="flex flex-row justify-between items-center">
          <span className="text-xl font-medium text-primaryText">Drivstoffrapport</span>
          <div className="md:hidden">
            <ExcelExportButton data={data} config={config} />
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-2 w-full md:w-auto">
          <DatePicker
            fromDate={fromMonth}
            toDate={toMonth}
            setFromDate={setFromMonth}
            setToDate={setToMonth}
            type="month"
            minDate={oldestMonth ? valueToDate(oldestMonth, 'month') : undefined}
            maxDate={newestMonth ? valueToDate(newestMonth, 'month') : undefined}
          />
          <div className="w-full md:w-auto md:flex-none md:self-end">
            <ConsolidateInvoiceSelect className="h-11 w-full" />
          </div>
          <div className="hidden md:block self-end">
            <ExcelExportButton className="h-11" data={data} config={config} />
          </div>
        </div>
      </div>

      <FuelReportTable isLoading={status === 'loading'} data={data} />
    </div>
  )
}

export default FuelReport
